// ═══════════════════════════════════════════════════════════════
// IncidentLocationMarker — drops a star pin at the active incident's
// lat/lng on any Leaflet map. Used as a constant reference point
// across all GeoOps map views.
// ═══════════════════════════════════════════════════════════════

import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Text, Group, Badge } from '@mantine/core';

interface Incident {
  id: number | string;
  name: string;
  latitude?: string;
  longitude?: string;
  incidentType?: string;
  status?: string;
  location?: string;
}

interface Props {
  incident: Incident | null | undefined;
}

// Build the star icon once and reuse — Leaflet caches by reference
const STAR_ICON = L.divIcon({
  className: 'incident-location-star',
  html: `
    <div style="
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
    ">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="#fbbf24"
        stroke="#92400e" stroke-width="1.5" stroke-linejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    </div>
  `,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

export default function IncidentLocationMarker({ incident }: Props) {
  if (!incident) return null;
  const lat = parseFloat(incident.latitude || '');
  const lng = parseFloat(incident.longitude || '');
  if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return null;

  return (
    <Marker position={[lat, lng]} icon={STAR_ICON}>
      <Popup minWidth={220}>
        <div style={{ fontFamily: '"DM Sans", sans-serif' }}>
          <Group gap={4} mb={4}>
            <Text size="xs" fw={700} c="#92400e" tt="uppercase">Incident Location</Text>
          </Group>
          <Text size="sm" fw={700} mb={2}>{incident.name}</Text>
          {incident.location && (
            <Text size="xs" c="dimmed" mb={4}>{incident.location}</Text>
          )}
          <Group gap={4}>
            {incident.incidentType && (
              <Badge size="xs" variant="light" color="orange">
                {incident.incidentType.replace(/_/g, ' ')}
              </Badge>
            )}
            {incident.status && (
              <Badge size="xs" variant="light"
                color={incident.status === 'active' ? 'red' : 'gray'}>
                {incident.status}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" mt={6}>
            {lat.toFixed(5)}, {lng.toFixed(5)}
          </Text>
        </div>
      </Popup>
    </Marker>
  );
}
